import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  RGR_LESSONS,
  RGR_PROGRAMS,
  RGR_SKILLS,
  RGR_GRADES,
  RGRLesson,
  RGRGrade,
} from './data/rgr-lessons';
import { CURRICULUM_PILOT_LESSONS } from './data/curriculum-pilot-lessons';
import { BRITANNICA_GALLERY_LESSONS, NEARPOD_GALLERY_LESSONS } from './data/publisher-gallery-adapters';

const SHADOW = '4px 4px 0px #1e1e1e';
const SHADOW_SM = '3px 3px 0px #1e1e1e';
const RGR_RED = '#C8102E';
const PRESSTO_PURPLE = '#5C4FB8';
const BRITANNICA_RED = '#CC0000';
const NEARPOD_BLUE = '#16A8F9';

const PROGRAM_BRAND_COLOR: Record<string, string> = {
  'Britannica Learning': BRITANNICA_RED,
  'Nearpod': NEARPOD_BLUE,
};

// ─── Lesson Card ───────────────────────────────────────────────────────────────
function LessonCard({ lesson }: { lesson: RGRLesson }) {
  const navigate = useNavigate();
  const isPressto = lesson.brand === 'Pressto';
  const brandColor = PROGRAM_BRAND_COLOR[lesson.program] ?? (isPressto ? PRESSTO_PURPLE : RGR_RED);
  const isLive = lesson.status === 'live';
  const [showComingSoon, setShowComingSoon] = useState(false);

  const handleClick = () => {
    if (lesson.templateUrl) {
      window.location.assign(lesson.templateUrl);
      return;
    }
    if (isLive) {
      navigate('/register');
      return;
    }
    setShowComingSoon(true);
    window.setTimeout(() => setShowComingSoon(false), 1800);
  };

  return (
    <Box sx={{ position: 'relative', overflow: 'visible' }}>
      {/* "New" sticker — lives OUTSIDE the card (which has overflow:hidden for the color band) */}
      {lesson.isNew && (
        <Box
          sx={{
            position: 'absolute',
            top: -14,
            left: 12,
            backgroundColor: '#FFD93D',
            color: '#1e1e1e',
            border: '2px solid #1e1e1e',
            borderRadius: '4px',
            px: 1.1,
            py: 0.35,
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 800,
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            boxShadow: '2px 2px 0px #1e1e1e',
            transform: 'rotate(-3deg)',
            zIndex: 5,
            pointerEvents: 'none',
          }}
        >
          New
        </Box>
      )}

      <Box
        sx={{
          position: 'relative',
          borderRadius: '8px',
          border: '2px solid #1e1e1e',
          boxShadow: SHADOW,
          backgroundColor: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'all 100ms ease-in-out',
          '&:hover': {
            boxShadow: '2px 2px 0px #1e1e1e',
            transform: 'translate(2px, 2px)',
          },
        }}
      >

      {/* Status badge */}
      <Box
        sx={{
          position: 'absolute',
          top: 18,
          right: 14,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          backgroundColor: isLive ? '#E8F5E9' : '#F5F5F5',
          color: isLive ? '#2E7D32' : '#666',
          border: `1.5px solid ${isLive ? '#2E7D32' : '#999'}`,
          borderRadius: '999px',
          px: 1,
          py: 0.25,
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 700,
          fontSize: '0.6rem',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          zIndex: 2,
        }}
      >
        <Box
          component="span"
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: isLive ? '#2E7D32' : '#999',
            display: 'inline-block',
          }}
        />
        {isLive ? 'Live' : 'Coming Soon'}
      </Box>

      {/* Thin top accent band */}
      <Box sx={{ height: 8, backgroundColor: brandColor }} />

      {/* Program wordmark + unit */}
      <Box sx={{ pt: 3, pb: 2, px: 3 }}>
        <Typography
          sx={{
            fontFamily: 'Montserrat, Inter, sans-serif',
            fontWeight: 800,
            fontSize: '1.25rem',
            color: brandColor,
            letterSpacing: '-0.01em',
            lineHeight: 1.1,
            fontStyle: isPressto ? 'italic' : 'normal',
          }}
        >
          {lesson.program}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 700,
            fontSize: '0.8rem',
            color: '#1e1e1e',
            mt: 0.5,
          }}
        >
          {lesson.unit}
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography
          sx={{
            fontFamily: 'Lato, Inter, sans-serif',
            fontSize: '0.8rem',
            color: '#666',
            mb: 1.5,
          }}
        >
          {lesson.gradeBand} | {lesson.skill}:
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Montserrat, Inter, sans-serif',
            fontWeight: 700,
            fontSize: '1.05rem',
            color: '#1e1e1e',
            lineHeight: 1.35,
            mb: 3,
            flex: 1,
          }}
        >
          {lesson.title}
        </Typography>
        {lesson.source && (
          <Box
            sx={{
              alignSelf: 'flex-start',
              mb: 2,
              px: 1,
              py: 0.4,
              borderRadius: '6px',
              border: '1.5px solid',
              borderColor: lesson.source === 'verbatim' ? '#2E7D32' : '#5C4FB8',
              backgroundColor: lesson.source === 'verbatim' ? '#E8F5E9' : '#EFEDFB',
              color: lesson.source === 'verbatim' ? '#2E7D32' : '#5C4FB8',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              fontSize: '0.6rem',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {lesson.source === 'verbatim' ? 'Source prompt' : 'Pressto original'}
          </Box>
        )}
        <Box
          onClick={handleClick}
          sx={{
            borderTop: '1px solid #E5E5E5',
            pt: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            cursor: 'pointer',
            color: showComingSoon ? '#666' : PRESSTO_PURPLE,
            transition: 'color 120ms ease-in-out',
            '&:hover': { color: showComingSoon ? '#666' : '#3D32A8' },
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              fontSize: '0.85rem',
            }}
          >
            {showComingSoon ? 'Coming soon!' : 'View Writing Activity'}
          </Typography>
          {!showComingSoon && <VisibilityIcon sx={{ fontSize: '1rem' }} />}
        </Box>
      </Box>
      </Box>
    </Box>
  );
}

// ─── Filter pill (search input or select) ──────────────────────────────────────
function FilterShell({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        border: '2px solid #1e1e1e',
        borderRadius: '999px',
        boxShadow: SHADOW_SM,
        px: 2.5,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minHeight: 44,
      }}
    >
      {children}
    </Box>
  );
}

// ─── RGRGallery ────────────────────────────────────────────────────────────────
// Shared lesson catalog used by both the public /curriculum/rgr landing page
// and the in-app /gallery/templates view.
export default function RGRGallery() {
  const [query, setQuery] = useState('');
  const [program, setProgram] = useState<string>('All');
  const [skill, setSkill] = useState<string>('');
  const [grade, setGrade] = useState<string>('');

  const ALL_LESSONS = useMemo(
    () => [...RGR_LESSONS, ...CURRICULUM_PILOT_LESSONS, ...BRITANNICA_GALLERY_LESSONS, ...NEARPOD_GALLERY_LESSONS],
    [],
  );

  const filtered = useMemo(() => {
    return ALL_LESSONS.filter((l) => {
      if (program && program !== 'All' && l.program !== program) return false;
      if (skill && l.skill !== skill) return false;
      if (grade) {
        const supportsGrade = l.grade === grade || (l.grades && l.grades.includes(grade as RGRGrade));
        if (!supportsGrade) return false;
      }
      if (query && !l.title.toLowerCase().includes(query.toLowerCase()) && !l.unit.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [ALL_LESSONS, query, program, skill, grade]);

  return (
    <Box
      sx={{
        backgroundColor: PRESSTO_PURPLE,
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 6 },
        borderTop: '2px solid #1e1e1e',
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: 'Montserrat, Inter, sans-serif',
            fontWeight: 800,
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            color: '#FFFFFF',
            textAlign: 'center',
            mb: 4,
          }}
        >
          Full Catalog of Aligned Writing Activities
        </Typography>

        {/* Filters */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr 1fr' },
            gap: 2,
            mb: 5,
            maxWidth: 1000,
            mx: 'auto',
          }}
        >
          <FilterShell>
            <SearchIcon sx={{ color: '#999', fontSize: '1.1rem' }} />
            <InputBase
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{
                flex: 1,
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.95rem',
                color: '#1e1e1e',
              }}
            />
          </FilterShell>

          <FilterShell>
            <FormControl variant="standard" sx={{ flex: 1 }}>
              <Select
                value={program}
                onChange={(e) => setProgram(e.target.value)}
                disableUnderline
                sx={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.95rem',
                  color: '#1e1e1e',
                  '& .MuiSelect-select:focus': { backgroundColor: 'transparent' },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {RGR_PROGRAMS.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </FilterShell>

          <FilterShell>
            <FormControl variant="standard" sx={{ flex: 1 }}>
              <Select
                value={skill}
                onChange={(e) => setSkill(e.target.value)}
                displayEmpty
                disableUnderline
                sx={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.95rem',
                  color: skill ? '#1e1e1e' : '#888',
                  '& .MuiSelect-select:focus': { backgroundColor: 'transparent' },
                }}
              >
                <MenuItem value="">Subject / Skill</MenuItem>
                {RGR_SKILLS.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </FilterShell>

          <FilterShell>
            <FormControl variant="standard" sx={{ flex: 1 }}>
              <Select
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                displayEmpty
                disableUnderline
                sx={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.95rem',
                  color: grade ? '#1e1e1e' : '#888',
                  '& .MuiSelect-select:focus': { backgroundColor: 'transparent' },
                }}
              >
                <MenuItem value="">Grade</MenuItem>
                {RGR_GRADES.map((g) => (
                  <MenuItem key={g} value={g}>{g}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </FilterShell>
        </Box>

        {/* Cards grid */}
        {filtered.length === 0 ? (
          <Typography
            sx={{
              fontFamily: 'Lato, sans-serif',
              color: 'rgba(255,255,255,0.85)',
              textAlign: 'center',
              py: 8,
              fontSize: '1.1rem',
            }}
          >
            No lessons match those filters yet.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {filtered.map((lesson) => (
              <LessonCard key={`${lesson.program}-${lesson.title}`} lesson={lesson} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
